import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import WarningIcon from '@material-ui/icons/Warning';

import { unoTheme } from '../../assets/styles/unoTheme';
import MyTable from '../utils/MyTable';
import UnoButton from '../utils/UnoButton';
import { applyMaskAmount, applyMaskCnpj, getMonthName } from '../utils/utils';
import {
    MAX_DIARYS_IN_MONTH,
    MIN_DIARYS_IN_MONTH,
    countAnalysisIssues,
} from '../utils/portfolioAnalysisUtils';

const useStyles = makeStyles((theme) => ({

    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        }
    },

    subtitle: {
        color: unoTheme.defaultTextColor,
        fontSize: '14px',
    },

    contentModal: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
    },

    actionsFlexRight: {
        display: 'flex',
        alignItems: 'center',
    },

    summaryChip: {
        display: 'inline-block',
        padding: '4px 14px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },

    countChip: {
        display: 'inline-block',
        padding: '2px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        marginLeft: theme.spacing(1),
    },

    section: {
        borderLeft: 'solid 4px',
        borderRadius: '4px',
        background: unoTheme.secondaryTuplaTable,
        padding: '12px 16px',
        marginBottom: theme.spacing(2),
    },

    sectionHeader: {
        display: 'flex',
        alignItems: 'center',

        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        }
    },

    sectionTitle: {
        fontSize: '15px',
        fontWeight: 600,
        color: 'black',
    },

    sectionDescription: {
        fontSize: '12px',
        color: unoTheme.defaultTextColor,
        marginTop: '2px',
        marginLeft: '32px',
    },

    sectionOk: {
        fontSize: '12px',
        color: unoTheme.successColor,
        marginTop: '4px',
        marginLeft: '32px',
    },

    sectionTable: {
        marginTop: theme.spacing(1),
        background: 'white',
        borderRadius: '10px',
    },

    green: {
        color: unoTheme.greenMoney,
    },

    expectedCell: {
        color: unoTheme.defaultTextColor,
    },
}));

const SEVERITY_ERROR = 'error';
const SEVERITY_WARNING = 'warning';

function getSeverityColor(severity, hasIssues) {

    if (!hasIssues) return unoTheme.successColor;
    return severity === SEVERITY_ERROR ? unoTheme.errorColor : unoTheme.warningColorStrong;
}

/**
 * Rotulo amigavel de um ativo. Como as validacoes de saldo trazem apenas o
 * asset_id, os nomes vem de um mapa montado a parte (assetLabelsMap).
 */
function getAssetLabel(assetId, assetLabelsMap) {

    const info = assetLabelsMap ? assetLabelsMap[assetId] : null;
    return info && info.fund_name ? info.fund_name : ('Ativo #' + assetId);
}

function getAssetCnpj(assetId, assetLabelsMap) {

    const info = assetLabelsMap ? assetLabelsMap[assetId] : null;
    return info && info.cnpj ? applyMaskCnpj(info.cnpj) : '-';
}

function getAssetAccount(assetId, assetLabelsMap) {

    const info = assetLabelsMap ? assetLabelsMap[assetId] : null;
    return info && info.number_account ? info.number_account : '-';
}

function getAccountInfo(accountId, accountLabelsMap) {

    return (accountLabelsMap ? accountLabelsMap[accountId] : null) || {};
}

function formatBalances(group) {

    return group.map(row => applyMaskAmount(row.balance, true)).join('  |  ');
}

/**
 * Bloco de uma verificacao. O cabecalho e sempre exibido: em verde quando nada
 * foi encontrado e em amarelo/vermelho quando ha ocorrencias.
 */
function AnalysisSection({ title, description, severity, count, children }) {

    const classes = useStyles();

    const hasIssues = count > 0;
    const color = getSeverityColor(severity, hasIssues);

    return (
        <div className={classes.section} style={{ borderLeftColor: color }}>

            <div className={classes.sectionHeader}>
                {
                    !hasIssues ?
                        <CheckCircleIcon style={{ color: color }} />
                        :
                        (severity === SEVERITY_ERROR ?
                            <ReportProblemIcon style={{ color: color }} />
                            :
                            <WarningIcon style={{ color: color }} />)
                }

                <span className={classes.sectionTitle}>{title}</span>

                {
                    hasIssues ?
                        <span
                            className={classes.countChip}
                            style={{ background: color, color: 'white' }}>
                            {count}
                        </span>
                        : null
                }
            </div>

            {
                description ?
                    <div className={classes.sectionDescription}>{description}</div>
                    : null
            }

            {
                hasIssues ?
                    <div className={classes.sectionTable}>{children}</div>
                    :
                    <div className={classes.sectionOk}>{'Nenhum problema encontrado'}</div>
            }

        </div>
    )
}

function DuplicatedAssetsTable({ groups, assetLabelsMap, keyPrefix }) {

    const classes = useStyles();

    return (
        <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
            <colgroup>
                <col width="35%" />
                <col width="20%" />
                <col width="12%" />
                <col width="10%" />
                <col width="23%" />
            </colgroup>
            <TableHead>
                <TableRow>
                    <TableCell align="left">{'Ativo'}</TableCell>
                    <TableCell align="center">{'CNPJ'}</TableCell>
                    <TableCell align="center">{'CC'}</TableCell>
                    <TableCell align="center">{'Registros'}</TableCell>
                    <TableCell align="right">{'Saldos'}</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {groups.map((group, index) => {

                    const assetId = group[0].asset_id;

                    return (
                        <TableRow key={keyPrefix + index}>
                            <TableCell align="left">{getAssetLabel(assetId, assetLabelsMap)}</TableCell>
                            <TableCell align="center">{getAssetCnpj(assetId, assetLabelsMap)}</TableCell>
                            <TableCell align="center">{getAssetAccount(assetId, assetLabelsMap)}</TableCell>
                            <TableCell align="center">{group.length}</TableCell>
                            <TableCell align="right" className={classes.green}>
                                {formatBalances(group)}
                            </TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </MyTable>
    )
}

export default function PortfolioAnalysisModal({ analysis, period, open, closeModal }) {

    const classes = useStyles();

    const {
        missingDiaryMonths = [],
        invalidDiaryCountMonths = [],
        duplicatedCurrentBalances = [],
        duplicatedNextBalances = [],
        duplicatedAccountBalances = [],
        equalBalancePairs = [],
        duplicatedLocalQuotas = [],
        assetLabelsMap,
        accountLabelsMap,
        nextPeriod,
        previousPeriod,
    } = analysis || {};

    const totalIssues = countAnalysisIssues(analysis);

    const periodLabel = period && period.month && period.year ?
        getMonthName(period.month) + '/' + period.year : '';

    const nextPeriodLabel = nextPeriod && nextPeriod.month && nextPeriod.year ?
        getMonthName(nextPeriod.month) + '/' + nextPeriod.year : 'mês seguinte';

    const previousPeriodLabel = previousPeriod && previousPeriod.month && previousPeriod.year ?
        getMonthName(previousPeriod.month) + '/' + previousPeriod.year : 'mês anterior';

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'lg'}
            open={open}
            PaperProps={{ style: { height: '90vh' } }}
            onClose={closeModal}>

            <DialogTitle className={classes.dialogTitle}>
                <div>
                    {'Análise da carteira'}
                    <Typography className={classes.subtitle}>
                        {periodLabel}
                    </Typography>
                </div>

                <span
                    className={classes.summaryChip}
                    style={totalIssues > 0 ?
                        { background: unoTheme.errorColor, color: 'white' }
                        : { background: unoTheme.successColor, color: 'white' }}>
                    {totalIssues > 0 ?
                        totalIssues + (totalIssues > 1 ? ' inconsistências' : ' inconsistência')
                        : 'Nenhuma inconsistência'}
                </span>
            </DialogTitle>

            <DialogContent className={classes.contentModal}>

                <AnalysisSection
                    title={'Meses sem diário'}
                    description={'Meses fechados do ano que não possuem nenhum diário registrado.'}
                    severity={SEVERITY_ERROR}
                    count={missingDiaryMonths.length}>

                    <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
                        <colgroup>
                            <col width="100%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">{'Mês'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {missingDiaryMonths.map((month, index) => (
                                <TableRow key={'missing-diary-month-' + index}>
                                    <TableCell align="left">
                                        {getMonthName(month) + '/' + (period ? period.year : '')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </MyTable>
                </AnalysisSection>

                <AnalysisSection
                    title={'Quantidade de diários fora do esperado'}
                    description={'Meses com menos de ' + MIN_DIARYS_IN_MONTH + ' ou mais de ' + MAX_DIARYS_IN_MONTH + ' diários.'}
                    severity={SEVERITY_WARNING}
                    count={invalidDiaryCountMonths.length}>

                    <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
                        <colgroup>
                            <col width="40%" />
                            <col width="20%" />
                            <col width="20%" />
                            <col width="20%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">{'Mês'}</TableCell>
                                <TableCell align="center">{'Qtd. diários'}</TableCell>
                                <TableCell align="center">{'Esperado'}</TableCell>
                                <TableCell align="right">{'Rent. mês'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invalidDiaryCountMonths.map((row, index) => (
                                <TableRow key={'invalid-diary-count-' + index}>
                                    <TableCell align="left">
                                        {getMonthName(row.month) + '/' + row.year}
                                    </TableCell>
                                    <TableCell align="center">{row.count}</TableCell>
                                    <TableCell align="center" className={classes.expectedCell}>
                                        {MIN_DIARYS_IN_MONTH + ' a ' + MAX_DIARYS_IN_MONTH}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.rent !== undefined && row.rent !== null ?
                                            applyMaskAmount(row.rent) + ' %' : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </MyTable>
                </AnalysisSection>

                <AnalysisSection
                    title={'Saldos de ativos duplicados em ' + periodLabel}
                    description={'Ativos com mais de um saldo registrado no mesmo mês.'}
                    severity={SEVERITY_ERROR}
                    count={duplicatedCurrentBalances.length}>

                    <DuplicatedAssetsTable
                        groups={duplicatedCurrentBalances}
                        assetLabelsMap={assetLabelsMap}
                        keyPrefix={'duplicated-current-balance-'} />
                </AnalysisSection>

                <AnalysisSection
                    title={'Saldos de ativos duplicados em ' + nextPeriodLabel}
                    description={'Ativos com mais de um saldo registrado no mês seguinte ao selecionado.'}
                    severity={SEVERITY_ERROR}
                    count={duplicatedNextBalances.length}>

                    <DuplicatedAssetsTable
                        groups={duplicatedNextBalances}
                        assetLabelsMap={assetLabelsMap}
                        keyPrefix={'duplicated-next-balance-'} />
                </AnalysisSection>

                <AnalysisSection
                    title={'Saldos de contas duplicados em ' + periodLabel}
                    description={'Contas com mais de um saldo registrado no mesmo mês.'}
                    severity={SEVERITY_ERROR}
                    count={duplicatedAccountBalances.length}>

                    <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
                        <colgroup>
                            <col width="35%" />
                            <col width="12%" />
                            <col width="15%" />
                            <col width="10%" />
                            <col width="28%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">{'Instituição'}</TableCell>
                                <TableCell align="center">{'Agência'}</TableCell>
                                <TableCell align="center">{'Conta'}</TableCell>
                                <TableCell align="center">{'Registros'}</TableCell>
                                <TableCell align="right">{'Saldos'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {duplicatedAccountBalances.map((group, index) => {

                                const accountId = group[0].account_id;
                                const info = getAccountInfo(accountId, accountLabelsMap);

                                return (
                                    <TableRow key={'duplicated-account-balance-' + index}>
                                        <TableCell align="left">
                                            {info.instituition_account || ('Conta #' + accountId)}
                                        </TableCell>
                                        <TableCell align="center">{info.agency || '-'}</TableCell>
                                        <TableCell align="center">{info.number_account || '-'}</TableCell>
                                        <TableCell align="center">{group.length}</TableCell>
                                        <TableCell align="right" className={classes.green}>
                                            {formatBalances(group)}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </MyTable>
                </AnalysisSection>

                <AnalysisSection
                    title={'Ativos distintos com saldo idêntico em ' + periodLabel}
                    description={'Possível replicação de saldo entre ativos diferentes.'}
                    severity={SEVERITY_WARNING}
                    count={equalBalancePairs.length}>

                    <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
                        <colgroup>
                            <col width="37%" />
                            <col width="37%" />
                            <col width="26%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">{'Ativo'}</TableCell>
                                <TableCell align="left">{'Ativo'}</TableCell>
                                <TableCell align="right">{'Saldo'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {equalBalancePairs.map((pair, index) => (
                                <TableRow key={'equal-balance-pair-' + index}>
                                    <TableCell align="left">
                                        {getAssetLabel(pair.a.asset_id, assetLabelsMap)}
                                    </TableCell>
                                    <TableCell align="left">
                                        {getAssetLabel(pair.b.asset_id, assetLabelsMap)}
                                    </TableCell>
                                    <TableCell align="right" className={classes.green}>
                                        {applyMaskAmount(pair.a.balance, true)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </MyTable>
                </AnalysisSection>

                <AnalysisSection
                    title={'Cotas locais duplicadas em ' + periodLabel + ' ou ' + previousPeriodLabel}
                    description={'Fundo com mais de uma linha de cotas locais no mesmo mês. A consulta de ativos da carteira lê as cotas do mês selecionado e do anterior, então a duplicidade repete o ativo e soma o saldo em dobro, distorcendo a cota da carteira.'}
                    severity={SEVERITY_ERROR}
                    count={duplicatedLocalQuotas.length}>

                    <MyTable sizeheaders="verySmall" size="small" heightAuto noscroll>
                        <colgroup>
                            <col width="37%" />
                            <col width="20%" />
                            <col width="13%" />
                            <col width="10%" />
                            <col width="20%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">{'Fundo'}</TableCell>
                                <TableCell align="center">{'CNPJ'}</TableCell>
                                <TableCell align="center">{'Mês'}</TableCell>
                                <TableCell align="center">{'Registros'}</TableCell>
                                <TableCell align="right">{'Ids'}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {duplicatedLocalQuotas.map((row, index) => (
                                <TableRow key={'duplicated-local-quota-' + index}>
                                    <TableCell align="left">
                                        {row.fund_name ? row.fund_name : ('Fundo #' + row.fund_id)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.cnpj ? row.cnpj : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {getMonthName(row.month) + '/' + row.year}
                                    </TableCell>
                                    <TableCell align="center">{row.qtd}</TableCell>
                                    <TableCell align="right" className={classes.expectedCell}>
                                        {(row.ids || []).join(', ')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </MyTable>
                </AnalysisSection>

            </DialogContent>

            <DialogActions className={classes.actionsFlexRight}>
                <UnoButton
                    float="right"
                    onClick={closeModal}
                    color="secondary">
                    {'Fechar'}
                </UnoButton>
            </DialogActions>

        </Dialog>
    );
}
